import { SideReanimatedEvent } from '../../events/GameEvents';
import { ForceCalculator } from './ForceCalculator';
import { ReanimationReason } from '../reanimation/ReanimationManager';
import { trapLogger } from '../../utils/TrapLogger';
/**
 * Trap Situations according to design document
 */
export var TrapSituationType;
(function (TrapSituationType) {
    /**
     * Situation I: Player surrounded but CAN escape
     * Player chooses who gets the territory
     */
    TrapSituationType["ESCAPABLE"] = "escapable";
    /**
     * Situation II: Player surrounded and CANNOT escape
     * Territory goes to strongest surrounding side
     */
    TrapSituationType["FORCE_BASED"] = "force_based";
    /**
     * Situation III: Player surrounded by single color
     * Instant capture by that color
     */
    TrapSituationType["SINGLE_COLOR"] = "single_color";
})(TrapSituationType || (TrapSituationType = {}));
/**
 * TrapResolver - Handles all trap situation logic
 * Implements the 3 situations from design document
 */
export class TrapResolver {
    board;
    eventBus;
    forceCalculator; // ✅ Public для использования в MoveExecutor.makeAIChoice()
    // Moves a side has this turn. The orchestrator wires RoundManager.getMovesForSide;
    // a bare resolver reads the same rule off the board's round.
    movesForSide = (sideId) => sideId === 1 && this.board.round === 2 ? 2 : this.board.getMaxMovesPerRound();
    constructor(board, eventBus) {
        this.board = board;
        this.eventBus = eventBus;
        this.forceCalculator = new ForceCalculator(board);
    }
    setMovesForSide(movesForSide) {
        this.movesForSide = movesForSide;
    }
    /**
     * Analyzes trap situation when a side moves into surrounded position
     */
    analyzeSituation(position, trappedSide) {
        trapLogger.logAnalysisStart(position.toString(), trappedSide.id, this.board.round, 0);
        const surroundingSides = this.getSurroundingSides(position);
        trapLogger.log(`Raw surrounding sides: [${surroundingSides}]`);
        const uniqueSides = new Set(surroundingSides);
        // Remove trapped side from surrounding sides (they can't capture themselves)
        uniqueSides.delete(trappedSide.id);
        const surroundingArray = Array.from(uniqueSides);
        trapLogger.log(`Unique surrounding sides (excluding trapped): [${surroundingArray}]`);
        // Situation III: Single color surrounding (monopoly trap)
        if (surroundingArray.length === 1) {
            trapLogger.log('Situation III: Monopoly trap by side', surroundingArray[0]);
            const result = {
                type: TrapSituationType.SINGLE_COLOR,
                trappedSide: trappedSide.id,
                trappedPosition: position,
                surroundingSides: surroundingArray,
                canEscape: false,
                dominantSide: surroundingArray[0],
            };
            trapLogger.logAnalysisEnd('SINGLE_COLOR (Monopoly)', result);
            return result;
        }
        // Check if side can escape (has remaining moves AND can move FROM trapped position)
        const canEscape = this.checkEscapePossibility(position, trappedSide);
        // Calculate forces for all situations (not just force-based)
        // This is needed to determine if there's a clear winner or parity
        const forces = this.forceCalculator.calculateForces(position, trappedSide.id);
        const forcePriority = new Map();
        for (const force of forces) {
            forcePriority.set(force.sideId, force.totalForce);
        }
        // Determine winner using cascading algorithm
        const forceWinner = this.forceCalculator.determineWinner(forces);
        // Situation I: Can escape (has moves + can move from trap position)
        // FIX: Player ALWAYS gets choice when canEscape=true, regardless of force balance
        // According to design doc: "Игрок выбирает кому передать (всплывает окно)"
        // Force calculation is only used for Situation II (cannot escape)
        if (canEscape) {
            trapLogger.log('Situation I: Escapable trap - player ALWAYS chooses (regardless of force balance)');
            trapLogger.log(`Force balance info: winner would be ${forceWinner}, but player decides`);
            return {
                type: TrapSituationType.ESCAPABLE,
                trappedSide: trappedSide.id,
                trappedPosition: position,
                surroundingSides: surroundingArray,
                canEscape: true,
                forcePriority, // Info only - player makes final decision
            };
        }
        // Situation II: Force-based resolution (cannot escape)
        trapLogger.log('Situation II: Force-based resolution');
        trapLogger.log(`Trapped side has ${trappedSide.activeFields.size} active fields`);
        trapLogger.log(`Can escape: ${canEscape}`);
        trapLogger.log(`Force calculation results: ${forces.length} sides with forces`);
        return {
            type: TrapSituationType.FORCE_BASED,
            trappedSide: trappedSide.id,
            trappedPosition: position,
            surroundingSides: surroundingArray,
            canEscape: false,
            forcePriority,
        };
    }
    /**
     * Resolves trap based on player choice (Situation I)
     */
    resolveByChoice(situation, choice) {
        if (situation.type !== TrapSituationType.ESCAPABLE) {
            throw new Error('Choice resolution only for escapable traps');
        }
        const territory = this.board.getTerritoryAt(situation.trappedPosition);
        if (territory) {
            // FIXED: Remove territory from the trapped side's collection before transferring
            // This ensures the trapped side loses the territory when player chooses who gets it
            // Ходовое поле = 30 очков независимо от типа ловушки. Считаем ДО
            // loseTerritory(), которая вычищает позицию из turnActiveFields.
            // Раньше здесь флаг не передавался вовсе, и ESCAPABLE-ловушка всегда
            // давала 10 очков — тогда как FORCE_BASED и SINGLE_COLOR давали 30.
            // Ветку выбирает canEscape, который «затухает» по мере заполнения доски,
            // поэтому один и тот же захват стоил то 10, то 30.
            const wasActiveField = this.board.isActiveFieldOf(situation.trappedSide, situation.trappedPosition);
            const trappedSideObj = this.board.getSide(situation.trappedSide);
            if (trappedSideObj) {
                trappedSideObj.loseTerritory(situation.trappedPosition);
            }
            territory.capture(choice.giveTerritoryTo, wasActiveField);
            territory.vacate(); // Remove trapped piece
            // Территория захвачена через ловушку — становится активной НЕМЕДЛЕННО
            const receivingSide = this.board.getSide(choice.giveTerritoryTo);
            if (receivingSide) {
                receivingSide.captureTerritory(situation.trappedPosition);
                receivingSide.addTrapCapturedField(situation.trappedPosition);
                // Сделать поле ходовым сразу для нового владельца
                receivingSide.addTurnActiveField(situation.trappedPosition);
                receivingSide.setActiveField(situation.trappedPosition, true);
                // Поставить фигуру на поле
                territory.occupy(choice.giveTerritoryTo);
            }
            // Check for reanimation opportunity
            this.checkAndReanimate(choice.giveTerritoryTo, situation.trappedPosition, situation.trappedSide, ReanimationReason.TRAP_CHOICE);
        }
        return {
            territoryCapturedBy: choice.giveTerritoryTo,
            sideEliminated: this.hasNoActiveFieldLeft(situation.trappedSide),
        };
    }
    /**
     * Resolves trap based on force calculation (Situation II)
     */
    resolveByForce(situation) {
        if (situation.type !== TrapSituationType.FORCE_BASED) {
            throw new Error('Force resolution only for force-based traps');
        }
        const winner = this.determineForceWinner(situation);
        if (winner === null) {
            // Parity - burn the territory
            const territory = this.board.getTerritoryAt(situation.trappedPosition);
            if (territory) {
                // IMPORTANT: Remove territory from the trapped side's collection before burning
                const trappedSideObj = this.board.getSide(situation.trappedSide);
                if (trappedSideObj) {
                    trappedSideObj.loseTerritory(situation.trappedPosition);
                }
                territory.burn();
                territory.vacate();
            }
            return {
                territoryCapturedBy: -1, // No one captures
                sideEliminated: this.hasNoActiveFieldLeft(situation.trappedSide),
                burned: true,
            };
        }
        const territory = this.board.getTerritoryAt(situation.trappedPosition);
        if (territory) {
            // Один и тот же предикат, что и в остальных путях захвата. Считаем ДО
            // loseTerritory(). Раньше здесь стоял хардкод true — правильный ответ,
            // полученный неправильным способом (30 очков даже за не-ходовое поле).
            const wasActiveField = this.board.isActiveFieldOf(situation.trappedSide, situation.trappedPosition);
            // IMPORTANT: Remove territory from the trapped side's turnActiveFields
            const trappedSideObj = this.board.getSide(situation.trappedSide);
            if (trappedSideObj) {
                trappedSideObj.loseTerritory(situation.trappedPosition);
            }
            // #D2: передаём флаг прямо в capture() (capture() сбрасывает его по умолчанию)
            territory.capture(winner, wasActiveField);
            territory.vacate();
            // Территория захвачена через ловушку — становится активной НЕМЕДЛЕННО
            const winningSide = this.board.getSide(winner);
            if (winningSide) {
                winningSide.captureTerritory(situation.trappedPosition);
                winningSide.addTrapCapturedField(situation.trappedPosition);
                winningSide.addTurnActiveField(situation.trappedPosition);
                winningSide.setActiveField(situation.trappedPosition, true);
                territory.occupy(winner);
            }
            // Check for reanimation opportunity
            this.checkAndReanimate(winner, situation.trappedPosition, situation.trappedSide, ReanimationReason.TRAP_CAPTURE);
        }
        return {
            territoryCapturedBy: winner,
            sideEliminated: this.hasNoActiveFieldLeft(situation.trappedSide),
        };
    }
    /**
     * Resolves instant capture trap (Situation III)
     */
    resolveInstant(situation) {
        if (situation.type !== TrapSituationType.SINGLE_COLOR) {
            throw new Error('Instant resolution only for single-color traps');
        }
        const territory = this.board.getTerritoryAt(situation.trappedPosition);
        if (territory && situation.dominantSide !== undefined) {
            // Тот же предикат, что и в остальных путях захвата. Считаем ДО loseTerritory().
            const wasActiveField = this.board.isActiveFieldOf(situation.trappedSide, situation.trappedPosition);
            // IMPORTANT: Remove territory from the trapped side's turnActiveFields
            const trappedSideObj = this.board.getSide(situation.trappedSide);
            if (trappedSideObj) {
                trappedSideObj.loseTerritory(situation.trappedPosition);
            }
            // #D2: передаём флаг прямо в capture() (capture() сбрасывает его по умолчанию)
            territory.capture(situation.dominantSide, wasActiveField);
            territory.vacate();
            // Территория захвачена через ловушку — становится активной НЕМЕДЛЕННО
            const dominantSideObj = this.board.getSide(situation.dominantSide);
            if (dominantSideObj) {
                dominantSideObj.captureTerritory(situation.trappedPosition);
                dominantSideObj.addTrapCapturedField(situation.trappedPosition);
                dominantSideObj.addTurnActiveField(situation.trappedPosition);
                dominantSideObj.setActiveField(situation.trappedPosition, true);
                territory.occupy(situation.dominantSide);
            }
            // Deactivate the source field that the trapped piece moved from
            if (situation.fromPosition) {
                const fromTerritory = this.board.getTerritoryAt(situation.fromPosition);
                if (fromTerritory) {
                    fromTerritory.setAsActiveField(false);
                }
                const trappedSideObj2 = this.board.getSide(situation.trappedSide);
                if (trappedSideObj2) {
                    trappedSideObj2.setActiveField(situation.fromPosition, false);
                    trappedSideObj2.removeTurnActiveField(situation.fromPosition);
                }
            }
            // Check for reanimation opportunity
            this.checkAndReanimate(situation.dominantSide, situation.trappedPosition, situation.trappedSide, ReanimationReason.TRAP_CAPTURE);
        }
        return {
            territoryCapturedBy: situation.dominantSide,
            sideEliminated: this.hasNoActiveFieldLeft(situation.trappedSide),
        };
    }
    /**
     * Gets surrounding sides (orthogonal positions)
     * Returns ALL surrounding sides for trap analysis
     */
    getSurroundingSides(position) {
        const neighbors = position.getOrthogonalNeighbors();
        const sides = [];
        for (const neighbor of neighbors) {
            const territory = this.board.getTerritoryAt(neighbor);
            if (territory?.owner !== null && territory?.owner !== undefined) {
                sides.push(territory.owner);
            }
        }
        return sides;
    }
    /**
     * Checks if trapped side can escape from the TRAPPED POSITION
     *
     * ИСПРАВЛЕНИЕ: Теперь проверяет возможность побега КОНКРЕТНО с позиции ловушки,
     * а не с любого активного поля стороны.
     *
     * Escapable = у стороны остались ходы И фигура может сделать ход конём С ПОЗИЦИИ ЛОВУШКИ
     *
     * @param trappedPosition - позиция где фигура попала в ловушку
     * @param side - сторона попавшая в ловушку
     */
    checkEscapePossibility(trappedPosition, side) {
        // 1 ход в раунде 1, 2 у стороны 1 в раунде 2, иначе 3 (RoundManager.getMovesForSide)
        const maxMovesPerRound = this.movesForSide(side.id);
        // ИСПРАВЛЕНИЕ: movesInCurrentRound уже включает ход в ловушку
        // Проверяем: были ли ходы ПОСЛЕ хода в ловушку?
        // Если сделано X ходов, и X < maxMovesPerRound, значит можно было сделать еще ходы
        const movesBeforeTrap = side.movesInCurrentRound; // Уже включает текущий ход
        const wouldHaveMovesLeft = movesBeforeTrap < maxMovesPerRound;
        if (!wouldHaveMovesLeft) {
            console.log(`[TrapResolver] Side ${side.id} has no remaining moves (made ${side.movesInCurrentRound}/${maxMovesPerRound})`);
            return false;
        }
        console.log(`[TrapResolver] Side ${side.id} still has moves (made ${side.movesInCurrentRound}/${maxMovesPerRound}, would have ${maxMovesPerRound - movesBeforeTrap} left)`);
        // ИСПРАВЛЕНИЕ: Проверяем только ходы С ПОЗИЦИИ ЛОВУШКИ, а не со всех активных полей
        // Фигура может сбежать только если она может сделать ход конём с текущей позиции
        const knightMoves = trappedPosition.getKnightMoves();
        for (const move of knightMoves) {
            const territory = this.board.getTerritoryAt(move);
            // Can move to unoccupied territories (not occupied by a piece)
            if (territory && territory.isAvailableForMovement(side.id)) {
                console.log(`[TrapResolver] Side ${side.id} can escape from ${trappedPosition.toString()} to ${move.toString()}`);
                return true;
            }
        }
        console.log(`[TrapResolver] Side ${side.id} cannot escape from ${trappedPosition.toString()} - no valid knight moves from trapped position`);
        return false;
    }
    // Note: isValidEscapeMove method was removed as it was unused
    // Force calculation is now handled by ForceCalculator class
    /**
     * Determines winner based on force calculation
     * ✅ ИСПРАВЛЕНИЕ: Пересчитываем силы с ForceCalculator вместо использования Map
     * Map содержит только totalForce, но determineWinner нужны компоненты (orth, diag, global)
     */
    determineForceWinner(situation) {
        // Recalculate forces with ForceCalculator to get component breakdown
        const forces = this.forceCalculator.calculateForces(situation.trappedPosition, situation.trappedSide);
        // Use ForceCalculator's cascading winner determination logic
        return this.forceCalculator.determineWinner(forces);
    }
    /**
     * A side that lost a field to a trap is out only when nothing is left to move
     * from: no turnActiveFields, the set Side.isActiveField reads (rule 13). With an
     * active field left it stays in, and whether it has a move from there is
     * TurnManager's call — a skip, or a suspend.
     */
    hasNoActiveFieldLeft(sideId) {
        return this.board.getSide(sideId)?.turnActiveFields.size === 0;
    }
    /**
     * Checks and reanimates a suspended or eliminated side when it receives territory via trap.
     * Eliminated sides are always revived immediately — the passed field becomes active right away.
     * A revival publishes SideReanimatedEvent with the side the field came from.
     */
    checkAndReanimate(sideId, position, bySide, reason) {
        const side = this.board.getSide(sideId);
        if (!side)
            return;
        // A forfeited side (surrendered / timed out of the game) must never come back,
        // not even by receiving territory through a trap (AUD-031). ReanimationManager
        // already guards this path; mirror it here so the trap route can't revive them.
        if (side.isPermanentlyOut) {
            trapLogger.log(`Side ${sideId} is permanently out — skipping trap reanimation at ${position.toString()}`);
            return;
        }
        if (side.isEliminated) {
            console.log(`[TrapResolver] Reanimating eliminated side ${sideId} after receiving territory at ${position.toString()}`);
            side.reanimate(position);
            trapLogger.log(`Side ${sideId} reanimated from ELIMINATED status after trap resolution at ${position.toString()}`);
            this.eventBus.publish(new SideReanimatedEvent(sideId, position, bySide, reason));
            return;
        }
        if (!side.isSuspended) {
            return;
        }
        // Always reanimate suspended sides when they receive a territory via trap.
        // The received field becomes their active field immediately regardless of
        // whether there are valid knight moves from it right now — they may have
        // moves in the next round or from other positions.
        console.log(`[TrapResolver] Reanimating suspended side ${sideId} after receiving territory at ${position.toString()}`);
        side.reanimate(position);
        trapLogger.log(`Side ${sideId} reanimated from SUSPENDED status after trap resolution at ${position.toString()}`);
        this.eventBus.publish(new SideReanimatedEvent(sideId, position, bySide, reason));
    }
}
//# sourceMappingURL=TrapResolver.js.map